import {
  Box,
  Container,
  Heading,
  UnorderedList,
  ListItem,
  Text,
} from "@chakra-ui/react";
function Privacy() {
  return (
    <Container p={[4, null, 8, 12]} maxW={["fit-content", "container.lg"]}>
      <Box sx={{ "& > *": { paddingBottom: "1rem" } }}>
        <Heading size="lg" as="h2">
          The Nature Conservancy&apos; Privacy Statement
        </Heading>
        <Text>
          <strong>Updated January 15, 2020</strong>
        </Text>
        <Text>
          The Nature Conservancy (the “<strong>Conservancy</strong>”) takes your
          privacy very seriously and cares about how your information is
          collected, used, stored, and shared. We are providing this Privacy
          Statement to explain our practices and the choices that you can make
          about the way your information is used by the Conservancy. The Privacy
          Statement describes our practices in connection with the information
          we collect through websites operated by us from which you are
          accessing this Privacy Statement (the “<strong>Websites</strong>”),
          through the software applications made available by us for use on or
          through computers and mobile devices (the “<strong>Apps</strong>”),
          through our social media pages that we control through which you are
          accessing this Privacy Statement (collectively, our “
          <strong>Social Media Pages</strong>”), as well as through
          HTML-formatted email messages that we send to you that link to this
          Privacy Statement and through offline interactions that we may have
          with you (collectively, including the Websites, the Apps and our
          Social Media Pages, providing personal curated content)( the “
          <strong>Services</strong>”).
        </Text>
        <Text fontStyle="italic">
          For U.S. users only—
          <strong>
            by visiting this website you are accepting the policies described in
            this statement and consenting to disclosure of Personal Information
            to trusted third parties{" "}
          </strong>
          including grantors, grantees, other charities, collaborative partners,
          and sponsors, to permit them to send you marketing communications,
          consistent with your choices.
          <b />
        </Text>
        <Heading size="md" as="h3" mt="4">
          Personal Information We Collect
        </Heading>
        <Text>
          “<strong>Personal information</strong>” is information that identifies
          you as an individual or relates to an identifiable individual.
        </Text>
        <UnorderedList>
          <ListItem>
            We collect the following Personal Information: Name, email address,
            postal address, phone number, profile picture, social media ID, and
            similar contact information and credit card or other payment
            information. If you choose to provide your payment or credit card
            information to the Conservancy, we will use that information for the
            purposes for which it was provided; the Conservancy does not store
            credit card or other payment information.
          </ListItem>
        </UnorderedList>
        <Heading size="md" as="h3" mt="4">
          How We Collect Personal Information
        </Heading>
        <Text>
          The Conservancy and our service providers collect Personal Information
          in a variety of ways, including:
        </Text>
        <UnorderedList>
          <ListItem>
            We collect Personal Information you provide to us over the phone, in
            email, through a face-to-face conversation, or through online and
            offline forms such as e-newsletters, surveys, donation forms, event
            registration/participation (including volunteering) and advocacy
            action alerts that you populate.
          </ListItem>
          <ListItem>
            We use third-party postal address, phone number, email address
            products, and other sources to provide and collect accurate and
            updated contact information.
          </ListItem>
          <ListItem>
            We collect information from social media platforms and other sources
            used by you or people with whom you are friends or otherwise
            connected on social media platforms.
          </ListItem>
        </UnorderedList>
        <Text>
          We need to collect Personal Information in order to provide the
          requested Services to you. If you do not provide the information
          requested, we may not be able to provide the Services. If you disclose
          any Personal Information relating to other people to us or to our
          service providers in connection with the Services, you represent that
          you have the authority to do so and to permit us to use the
          information in accordance with this Privacy Statement.
        </Text>
        <Heading size="md" as="h3" mt="4">
          How the Conservancy Uses Personal Information
        </Heading>
        <Text>
          The Conservancy and our service providers use Personal Information for
          legitimate business purposes including:
        </Text>
        <UnorderedList>
          <ListItem>
            <strong>
              Providing the functionality of the Services and fulfilling your
              requests
            </strong>
            <UnorderedList>
              <ListItem>
                Process and respond to inquiries and for the purposes for which
                you provided the information, such as to process donations,
                register or renew you as a donor or supporter, purchase
                merchandise, or receive information about a project, special
                event, and service.
              </ListItem>
              <ListItem>Respond to your questions and comments.</ListItem>
              <ListItem>
                Alert you to administrative information such as information
                regarding the Services&apos; features, and changes to our terms,
                conditions, and policies.
              </ListItem>
            </UnorderedList>
          </ListItem>
        </UnorderedList>
        <Text>
          We will do the above to manage our contractual relationship with you
          and/or to comply with a legal obligation.
        </Text>
        <UnorderedList>
          <ListItem>
            <strong>Providing personalized services.</strong>
            <UnorderedList>
              <ListItem>
                Personalize the content, improve the navigability of the
                Services, and customize your experience on the Services,
                including targeted content, offers, and advertising on the
                Services, or via email or text message.
              </ListItem>
              <ListItem>
                Notify you about special programs, offers, products and services
                that we believe will be of interest to you, either from the
                Conservancy or on behalf of our affiliates or other third
                parties.
              </ListItem>
            </UnorderedList>
          </ListItem>
        </UnorderedList>
        <Text>
          We will provide personalized services either with your consent or
          because we have a legitimate interest.
        </Text>
        <UnorderedList>
          <ListItem>
            <strong>
              Allowing you to participate in special programs, activities,
              events, or promotions.
            </strong>
            <UnorderedList>
              <ListItem>
                Facilitate your participation in sweepstakes, contests, and
                similar promotions and to administer these activities. Some of
                these activities have additional rules, which could contain
                additional information about how we use and disclose information
                including your Personal Information. We suggest that you read
                any such rules carefully.
              </ListItem>
            </UnorderedList>
          </ListItem>
        </UnorderedList>
        <Text>
          We will provide personalized services either with your consent or
          because we have a legitimate interest.
        </Text>
        <UnorderedList>
          <ListItem>
            <strong>Accomplishing our business purposes.</strong>
            <UnorderedList>
              <ListItem>
                Protect the security or integrity of the Services and the
                Conservancy, such as by protecting against fraud, unauthorized
                transactions, claims and other liabilities, and managing risk
                exposure, including by identifying potential hackers and other
                unauthorized users.
              </ListItem>
              <ListItem>
                Conduct surveys to measure your satisfaction with aspects of the
                Conservancy&apos;s activities.
              </ListItem>
              <ListItem>
                Analyze data, usage trend identification, promotional campaign
                development, audit, fraud monitoring or prevention, new product
                or service development, Services&apos; enhancement, interaction
                improvement, or operating and expansion purposes.
              </ListItem>
              <ListItem>
                To enforce our{" "}
                <Text
                  display="inline"
                  color="primary.500"
                  textDecor="underline"
                >
                  <a href="/en-us/about-us/who-we-are/accountability/terms-of-use/">
                    Terms of Use
                  </a>{" "}
                </Text>
                or other program terms.
              </ListItem>
            </UnorderedList>
          </ListItem>
        </UnorderedList>
        <Text>
          We use this information to manage our contractual relationship with
          you, comply with a legal obligation, and/or because we have a
          legitimate interest.
        </Text>
        <UnorderedList>
          <ListItem>
            <strong>
              Aggregating and/or anonymizing Personal Information.
            </strong>
            <UnorderedList>
              <ListItem>
                If we aggregate and anonymize Personal Information the data will
                no longer be considered Personal Information and we may use it
                for any purpose.
              </ListItem>
            </UnorderedList>
          </ListItem>
        </UnorderedList>
        <UnorderedList>
          <ListItem>
            <strong>
              As otherwise described to you at the point of collection.
            </strong>
          </ListItem>
        </UnorderedList>
        <Text>
          All references to sharing data with third-parties in our Privacy
          Policy exclude originator&nbsp;mobile number&nbsp;opt-in data and
          consent;&nbsp;and<i>&nbsp;</i>we will not share this information with
          any third-parties&nbsp;for their own uses.
        </Text>
        <Heading size="md" as="h3" mt="4">
          How the Conservancy Discloses Personal Information
        </Heading>
        <Text>We disclose Personal Information:</Text>
        <UnorderedList>
          <ListItem>
            <strong>To our service providers:</strong> We disclose the
            information we collect from you to third parties who perform
            functions on our behalf such as website hosting, data analysis,
            payment processing, information technology and related
            infrastructure provisions, solicitation or acknowledgment of
            charitable gifts, aggregate analysis and marketing activities as
            well as professional advisors such as auditors and attorneys.
          </ListItem>
          <ListItem>
            <strong>To our affiliates:</strong> We share information with our
            related entities such as affiliates and subsidiaries for the
            purposes detailed in this Privacy Statement. Our affiliates and
            their respective locations are listed in this{" "}
            <a
              href="/content/dam/tnc/nature/en/documents/form-990-tax-return-fy17.pdf"
              target="_blank"
            >
              disclosure
            </a>
            .
          </ListItem>
          <ListItem>
            <strong>To other organizations:</strong> We share your information,
            excluding e-mail address and phone number, with unaffiliated third
            parties for their own uses. You may choose to opt out as described
            in “How You Can Control the Collection and Use of Your Information”
            below.
          </ListItem>
          <ListItem>
            <strong>You may elect to disclose Personal Information:</strong> If
            you post information or materials on our message boards, chat,
            profile pages, blogs, and other services to which you are able to
            post information and content (including, without limitation, our
            Social Media Pages). Please note that any information you post or
            disclose through these services will become public and may be
            available to other users and the general public. We urge you to be
            very careful when deciding to disclose any information on the
            Services.
          </ListItem>
        </UnorderedList>
        <Heading size="md" as="h3" mt="4">
          Other Uses and Disclosures
        </Heading>
        <Text>
          We also use and disclose your Personal Information if it would be
          necessary or appropriate, in ways other than those noted above,
          especially when we have a legal obligation or legitimate interest to
          do so.
          <br />
        </Text>
        <UnorderedList>
          <ListItem>
            <strong>To comply with applicable law:</strong>
            <UnorderedList>
              <ListItem>
                This can include laws outside your country of residence.
              </ListItem>
            </UnorderedList>
          </ListItem>
        </UnorderedList>
        <UnorderedList>
          <ListItem>
            <strong>
              To respond to requests from public and government authorities:
            </strong>
            <UnorderedList>
              <ListItem>
                This can include authorities outside your country of residence.
              </ListItem>
            </UnorderedList>
          </ListItem>
        </UnorderedList>
        <UnorderedList>
          <ListItem>
            <strong>To cooperate with law enforcement:</strong>
            <UnorderedList>
              <ListItem>
                For example, when we receive law enforcement requests and
                orders.
              </ListItem>
            </UnorderedList>
          </ListItem>
        </UnorderedList>
        <UnorderedList>
          <ListItem>
            <strong>For other legal reasons:</strong>
            <UnorderedList>
              <ListItem>To enforce our terms and conditions;</ListItem>
              <ListItem>
                To protect our rights, privacy, safety or property, and/or that
                of our affiliates, you or others.
              </ListItem>
            </UnorderedList>
          </ListItem>
        </UnorderedList>
        <UnorderedList>
          <ListItem>
            <strong>In connection with a sale or business transaction:</strong>
            <UnorderedList>
              <ListItem>
                In addition, we have legitimate interest to disclose or transfer
                your information to a third party in the event of any
                reorganization, merger, sale, joint venture, assignment,
                transfer or other disposition of all or any portion of our
                operations, assets (including in connection with any bankruptcy
                or similar proceedings).
              </ListItem>
            </UnorderedList>
          </ListItem>
        </UnorderedList>
        <Heading size="md" as="h3" mt="4">
          Other Information
        </Heading>
        <Text>
          “<strong>Other Information</strong>” is any information that does not
          reveal your specific identity or does not directly relate to an
          identifiable individual. If we are required to treat Other Information
          as Personal Information under applicable law, then we use and disclose
          it for the purposes for which we use and disclose Personal Information
          as detailed in this Privacy Statement.
        </Text>
        <Text>
          We collect Other Information such as IP address, browser type, mobile
          device type, content visited while on the Websites or Apps, Media
          Access Control (MAC) address, computer type (Windows or Macintosh),
          screen resolution, operating system name and version, device
          manufacturer and model, language, Internet browser type and version,
          or the duration of the use of the Services. Collecting browser
          activities or IP addresses, along with the time of the visit and the
          page(s) that were visited, are common practices, and are often
          performed automatically by many websites, applications and other
          services for such purposes as calculating usage levels, helping
          diagnose server problems, and administering the Services. In addition:
        </Text>
        <UnorderedList>
          <ListItem>
            We and our service providers collect information from the Apps that
            you use. We track and collect Apps&apos; usage data such as the date
            and time the Apps accesses our servers and what information and
            files have been downloaded to the Apps based on your device number.
          </ListItem>
        </UnorderedList>
        <UnorderedList>
          <ListItem>
            We collect information that can identify the physical location of
            your mobile device or geographic location of your device by, for
            example, using satellite, cell phone tower or Wi-Fi signals to
            provide you with personalized location-based services and content.
          </ListItem>
        </UnorderedList>
        <UnorderedList>
          <ListItem>
            We use “Cookies” and similar technology to obtain information about
            your visits to the Services or your responses to our e-mails or
            other electronic communications—both individually and in an
            aggregated form. Cookies are pieces of data placed on your device
            when you visit the Services that help us recognize you when you come
            back to the Website, allowing us, for example, to remember the email
            address and password you use when you log in on a specific computer.
            We also gather statistical information about use of the Services in
            order to continually improve their design and functionality,
            understand how they are used and assist us with resolving questions
            regarding them. Cookies further allow us to select which of our
            advertisements or offers are most likely to appeal to you and
            display them while you are on the Services. We also use cookies or
            other technologies to track responses to our online advertisements.
            At this time, we do not respond to Do Not Track requests.
            <br />
            <br />
            If you do not want information collected through the use of cookies,
            there is a simple procedure in most browsers that allows you to
            automatically decline cookies or be given the choice of declining or
            accepting the transfer to your computer of a particular cookie (or
            cookies) from a particular site. If, however, you do not accept
            cookies, you may experience some inconvenience in your use of the
            Services and you may not be able to access some of the features we
            offer. For example, we may not be able to recognize your computer,
            and you may need to log in every time you visit. You also may not
            receive advertising or other offers from us that are relevant to
            your interests and needs.
          </ListItem>
        </UnorderedList>
        <UnorderedList>
          <ListItem>
            We use third-party web and mobile app analytics and optimization
            services to improve the Services and enhance the visitor experience
            by, for example, recording mouse clicks, mouse movements, scrolling
            activity as well as text you type in the Services.
          </ListItem>
        </UnorderedList>
        <UnorderedList>
          <ListItem>
            We use pixel tags and other similar technologies to, among other
            things, track the actions of users of the Services, measure the
            success of our marketing campaigns, and compile statistics about use
            of the Services and response rates.
          </ListItem>
        </UnorderedList>
        <UnorderedList>
          <ListItem>
            We use Google Analytics and Adobe Analytics which use Cookies and
            other similar technologies to collect information about use of
            Services anonymously and report website trends. You can learn about
            Google&apos;s practices by visiting{" "}
            <a
              href="https://www.google.com/policies/privacy/partners/"
              target="_blank"
              rel="noreferrer"
            >
              www.google.com/policies/privacy/partners/
            </a>
            , and you may opt out by downloading the Google Analytics opt-out
            browser add-on, available at{" "}
            <a
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              rel="noreferrer"
            >
              https://tools.google.com/dlpage/gaoptout
            </a>
            . You can learn about Adobe&apos;s practices by visiting{" "}
            <a
              href="https://www.adobe.com/privacy/analytics.html"
              target="_blank"
              rel="noreferrer"
            >
              https://www.adobe.com/privacy/analytics.html
            </a>{" "}
            and you may opt out by visiting{" "}
            <a
              href="https://www.adobe.com/privacy/opt-out.html"
              target="_blank"
              rel="noreferrer"
            >
              https://www.adobe.com/privacy/opt-out.html
            </a>
            .
          </ListItem>
        </UnorderedList>
        <UnorderedList>
          <ListItem>
            We collect information about your interactions with email messages,
            such as the links clicked on and whether the messages were opened or
            forwarded.
          </ListItem>
        </UnorderedList>
        <UnorderedList>
          <ListItem>
            Aggregate information: We share aggregate statistics and other
            non-personally identifiable information with third parties for
            marketing, advertising, research, or similar purposes. These
            aggregate statistics will not allow anyone to identify you.
          </ListItem>
        </UnorderedList>
        <Text>
          We use and disclose Other Information for any purpose, except where we
          are required to do otherwise under applicable law. In some instances,
          we combine Other Information with Personal Information (such as
          combining your name with your location). If we do, we will treat the
          combined information as Personal Information as long as it is
          combined.
        </Text>
        <Heading size="md" as="h3" mt="4">
          Our Advertising
        </Heading>
        <Text>
          We may use third-party advertising companies to address advertisements
          that may be of interest to you when you access and use the Services
          and other websites, based on information relating to your access to
          and use of the Services and other websites. To do so, such companies
          may place or recognize a unique Cookie on your browser (including
          through use of pixel tags). They may also use these technologies,
          along with your Mobile Advertising ID and information they collect
          about your online use, to recognize you across the devices you use,
          such as a mobile phone and a laptop.
        </Text>
        <Text>
          This advertising may be directed to you because you visited or logged
          in to our website. When we generate this advertising, data we collect
          from you through online Cookies your Mobile Advertising ID, and other
          technologies may be combined with other de-identified data (such as a
          hashed, non-readable email or postal address) in order to tailor ads
          on your computer, device or Smart TV to you or your business based on
          your preferences and other information about you. To learn more about
          targeted advertising, these providers, or how you may opt out, please
          visit{" "}
          <Text display="inline" color="primary.500" textDecor="underline">
            <a
              href="http://www.networkadvertising.org/choices/"
              target="_blank"
              rel="noreferrer"
            >
              http://www.networkadvertising.org/choices/
            </a>{" "}
          </Text>
          or the DAA&apos;s consumer choice page at{" "}
          <Text display="inline" color="primary.500" textDecor="underline">
            <a
              href="http://www.aboutads.info/choices"
              target="_blank"
              rel="noreferrer"
            >
              http://www.aboutads.info/choices
            </a>
          </Text>
          .
        </Text>
        <Heading size="md" as="h3" mt="4">
          How You Can Control Collection and Use of Your Information
        </Heading>
        <UnorderedList>
          <ListItem>
            <strong>
              Personal Information Sharing with Other Organizations:{" "}
            </strong>
            You may, at any time, choose to opt out on a going forward basis of
            sharing your information for the purpose of renting or exchanging
            with unaffiliated third party organizations for their own uses by
            calling us at{" "}
            <Text display="inline" color="primary.500" textDecor="underline">
              <a href="tel:800-628-6860">(800) 628-6860</a>
            </Text>
            , or emailing us at{" "}
            <Text display="inline" color="primary.500" textDecor="underline">
              <a href="mailto:member@tnc.org">member@tnc.org</a>
            </Text>
            . We do not rent or exchange your e-mail address or phone number.
          </ListItem>
          <ListItem>
            <strong>Marketing Email Opt Out: </strong>You may, at any time,
            choose to opt out of receiving marketing-related emails by following
            the opt-out procedures at the bottom of our emails. Please allow 10
            business days for us to respond to your request.
          </ListItem>
          <ListItem>
            <strong>Mailing List: </strong>You may, at any time, choose to opt
            out of receiving mail from us by calling us at (800) 628- 6860 or
            emailing us at{" "}
            <Text display="inline" color="primary.500" textDecor="underline">
              <a href="mailto:member@tnc.org">member@tnc.org</a>
            </Text>
            . Please allow 30-60 days for your address to be removed from any
            mailings that are currently underway.
          </ListItem>
          <ListItem>
            <strong>Telemarketing: </strong>You may, at any time, choose to opt
            out of the Conservancy&apos;s residential telemarketing list by
            calling us at (800) 628-6860 or emailing us at{" "}
            <Text display="inline" color="primary.500" textDecor="underline">
              <a href="mailto:member@tnc.org">member@tnc.org</a>
            </Text>
            . Please allow 30 days for your telephone number to be removed from
            any programs that are currently underway.
          </ListItem>
        </UnorderedList>
        <Text>
          <strong>
            Accessing, Modifying, Updating, and Deleting Your Information:{" "}
          </strong>
          We strive to keep our records as accurate as possible. You may, under
          applicable laws, have a right to access, correct, restrict, update,
          modify or delete your Personal Information that you have previously
          provided to us at any time. To do so, or to request to receive an
          electronic copy of your Personal Information for purposes of
          transmitting it to another company (to the extent this right to data
          portability is provided to you by applicable law), please call us at
          (800) 628-6860 or{" "}
          <Text display="inline" color="primary.500" textDecor="underline">
            <a href="/en-us/about-us/contact-us/">contact us online</a>
          </Text>
          .
        </Text>
        <Text>
          In your request, please make clear what Personal Information you would
          like to have changed, whether you would like to have your Personal
          Information suppressed from our database or otherwise let us know what
          limitations you would like to put on our use of your Personal
          Information. For your protection, we may only implement requests with
          respect to the Personal Information associated with the particular
          email address that you use to send us your request, and we may need to
          verify your identity before implementing your request. We will try to
          comply with your request as soon as reasonably practicable.
        </Text>
        <Text>
          Please note that we may need to retain certain information for
          recordkeeping purposes and/or to complete any transactions that you
          began prior to requesting a change or deletion (e.g., when you make a
          donation or enter a promotion, you may not be able to change or delete
          the Personal Information provided until after the completion of such
          donation or promotion). There may also be residual information that
          will remain within our databases and other records, which will not be
          removed.
        </Text>
        <Heading size="md" as="h3" mt="4">
          Your California Privacy Rights
        </Heading>
        <Text>
          If you are a California resident and have provided Personal
          Information to us, you are entitled by California law to request
          information regarding certain categories of Personal Information we
          share with third parties for their direct marketing purposes. Your
          request should be submitted to: Attn: Member Care Team The Nature
          Conservancy 4245 N. Fairfax Drive Suite 100, Arlington, VA 22203.
          Within 30 days of receiving such a request, we will provide a response
          listing the categories of Personal Information disclosed to third
          parties, along with the names and addresses of those third parties,
          for their own direct marketing purposes during the previous year. This
          request may be made no more than once per calendar year, and we
          reserve our right not to respond to requests submitted to addresses
          other than the address specified herein.
        </Text>
        <Text>
          If you are a resident of California, under 18 and a registered user of
          the Services, you may ask us to remove content or information that you
          have posted to the Services by writing to{" "}
          <Text display="inline" color="primary.500" textDecor="underline">
            <a href="mailto:member@tnc.org">member@tnc.org</a>
          </Text>
          . Please note that your request does not ensure complete or
          comprehensive removal of the content or information, as, for example,
          some of your content may have been reposted by another user.
        </Text>
        <Heading size="md" as="h3" mt="4">
          How We Protect Information
        </Heading>
        <Text>
          The Conservancy uses a variety of security measures to protect your
          Personal Information. We employ appropriate and reasonable
          organizational, electronic, physical, administrative, and technical
          measures, including encryption, to help safeguard Personal Information
          from unauthorized access or disclosure.
        </Text>
        <Text>
          When we work with other companies to process credit card transactions,
          those companies also use encryption and other appropriate security
          measures. However, no transmission or electronic storage of
          information can be completely secure. If you have reason to believe
          that your use of the Services or engagement with us is no longer
          secure, please immediately notify us as described in the{" "}
          <strong>“Questions/Contact Information” </strong>section below.
        </Text>
        <Heading size="md" as="h3" mt="4">
          Retention Period
        </Heading>
        <Text>
          We retain Personal Information for the period necessary to fulfill the
          purposes outlined in this Privacy Statement unless a longer retention
          period is required or permitted by law.
        </Text>
        <Text>
          The criteria used to determine our retention periods include:
        </Text>
        <UnorderedList>
          <ListItem>
            The length of time we have an ongoing relationship with you and
            provide the Services to you (for example, for as long as you have an
            account with us or keep using the Services);
          </ListItem>
          <ListItem>
            Whether there is a legal obligation to which we are subject (for
            example, certain laws require us to keep records of your
            transactions for a certain period of time before we can delete
            them); or
          </ListItem>
          <ListItem>
            Whether retention is advisable in light of our legal position (such
            as in regard to applicable statutes of limitations, litigation or
            regulatory investigations).
          </ListItem>
        </UnorderedList>
        <Heading size="md" as="h3" mt="4">
          Children&apos;s Privacy
        </Heading>
        <Text>
          This Website is not directed toward children under 13 years of age nor
          does the Conservancy knowingly collect information from children under
          13, and we request that they do not submit any Personal Information
          through the Services.
        </Text>
        <Heading size="md" as="h3" mt="4">
          Cross-Border Transfer
        </Heading>
        <Text>
          The Services are controlled and operated by us from the United States
          and are not intended to subject us to the laws or jurisdiction of any
          state, country or territory other than that of the United States. Your
          Personal Information may be stored and processed in any country where
          we have facilities or in which we engage service providers, and by
          using the Services you consent to the transfer of information to
          countries outside of your country of residence, including the United
          States, which may have data protection rules that are different from
          those of your country. In certain circumstances, courts, law
          enforcement agencies, regulatory agencies or security authorities in
          those other countries may be entitled to access your Personal
          Information.
        </Text>
        <Text>
          If you are located in the European Economic Area (“EEA”): Some of the
          non-EEA countries are recognized by the European Commission as
          providing an adequate level of data protection according to EEA
          standards (the full list of these countries is available{" "}
          <a
            href="http://ec.europa.eu/justice/data-protection/international-transfers/adequacy/index_en.htm"
            target="_blank"
            rel="noreferrer"
          >
            here
          </a>
          . For transfers from the EEA to countries not considered adequate by
          the European Commission, we have put in place adequate measures, such
          as standard contractual clauses adopted by the European Commission to
          protect your Personal Information. You may obtain a copy of these
          measures by following this link:{" "}
          <a
            href="https://ec.europa.eu/info/law/law-topic/data-protection/data-transfers-outside-eu/model-contracts-transfer-personal-data-third-countries_en"
            target="_blank"
            rel="noreferrer"
          >
            https://ec.europa.eu/info/law/law-topic/data-protection/data-transfers-outside-eu/model-contracts-transfer-personal-data-third-countries_en
          </a>
          .
        </Text>
        <Heading size="md" as="h3" mt="4">
          Third Party Content and Links
        </Heading>
        <Text>
          Our Services may contain links to third-party websites, applications,
          or similar technology that are not associated with or over which the
          Conservancy has no control. Any access to and use of such linked
          websites is not governed by this Statement, but instead is governed by
          the privacy statements of those third-party websites. We are not
          responsible for the information practices or content of these other
          websites and/or applications.
        </Text>
        <Text>
          We are not responsible for the information collection, use, disclosure
          or security policies or practices of other organizations, such as
          Facebook, Apple, Google, Microsoft, RIM or any other app developer,
          app provider, social media platform provider, operating system
          provider, wireless service provider or device manufacturer, including
          with respect to any Personal Information you disclose to other
          organizations through or in connection with the Apps or our Social
          Media Pages.
        </Text>
        <Heading size="md" as="h3" mt="4">
          Third Party Payment Service
        </Heading>
        <Text>
          We will use a third-party payment service to process payments made
          through the Services. If you wish to make a payment through the
          Services, your Personal Information will be collected by such third
          party and not by us, and will be subject to the third party&apos;s
          privacy statement, rather than this Privacy Statement. We have no
          control over, and are not responsible for, this third party&apos;s
          collection, use and disclosure of your Personal Information.
        </Text>
        <Heading size="md" as="h3" mt="4">
          Sensitive Information
        </Heading>
        <Text>
          Unless explicitly requested, we ask that you not send us, and you not
          disclose, any sensitive Personal Information (e.g., social security
          numbers, information related to racial or ethnic origin, political
          opinions, religion or other beliefs, health, biometrics or genetic
          characteristics, criminal background or trade union membership) on or
          through the Services or otherwise to us.
        </Text>
        <Heading size="md" as="h3" mt="4">
          Changes to This Privacy Statement
        </Heading>
        <Text>
          The Conservancy may, from time to time, amend this Privacy Statement.
          We will post any revisions to the Services, and we advise you to check
          back periodically. The “Effective Date” reference at the top of this
          page indicates when we last revised this Privacy Statement. Any
          changes to this Privacy Statement will become effective when we post
          the revised Privacy Statement on the Services. Your continued use of
          the Services or engagement in the Interactions after we implement such
          changes will also constitute your affirmative consent to the revised
          Privacy Statement.
        </Text>
        <Heading size="md" as="h3" mt="4">
          Questions/Contact Information
        </Heading>
        <Text>
          If you have any questions, concerns, or suggestions regarding this
          Privacy Statement or our privacy practices, you may{" "}
          <Text display="inline" color="primary.500" textDecor="underline">
            <a href="/en-us/about-us/contact-us/">contact us online</a>
          </Text>
          , send us an email at{" "}
          <Text display="inline" color="primary.500" textDecor="underline">
            <a href="mailto:member@tnc.org">member@tnc.org</a>
          </Text>
          , call our Member Care Team toll-free at{" "}
          <Text display="inline" color="primary.500" textDecor="underline">
            <a href="tel:800-628-6860">1 (800) 628-6860</a>
          </Text>
          Monday through Friday between 9:00 am and 5:00pm Eastern Time, or
          write us at:
        </Text>
        <Text>
          Attn: Member Care Team
          <br />
          The Nature Conservancy
          <br />
          4245 N. Fairfax Drive
          <br />
          Suite 100
          <br />
          Arlington, VA 22203
        </Text>
        <Text>
          Because email communications are not always secure, please do not
          include credit card or other sensitive information in your emails to
          us.
        </Text>
        <Heading size="md" as="h3" mt="4">
          Additional Information for Individuals in the EEA
        </Heading>
        <Text>If you are located in the EEA, you also may:</Text>
        <UnorderedList>
          <ListItem>
            Contact our Chief Privacy Officer at{" "}
            <Text display="inline" color="primary.500" textDecor="underline">
              <a href="mailto:compliance@tnc.org">compliance@tnc.org</a>.
            </Text>
          </ListItem>
          <ListItem>
            Lodge a complaint with a data protection authority for your country
            or region, or where an alleged infringement of applicable data
            protection law occurs.
          </ListItem>
        </UnorderedList>
      </Box>
    </Container>
  );
}

export default Privacy;
